import { useState } from 'react'
import { cn } from '../lib/cn'

/**
 * Reusable pill segmented-control menu (extracted from manouk's LangyShowcase).
 * A rounded "track" with pill buttons; the active one fills ink-900/paper, the
 * rest are muted and darken on hover. A mono sub-caption under the track shows
 * the active option's tagline. Wire it to whatever options you want.
 *
 *   const TABS = [
 *     { id: 'generate', label: 'Generate', sub: 'production traces → scenarios', render: () => <Foo/> },
 *     { id: 'trace',    label: 'Trace',    sub: 'inspect every span',           render: () => <LangyTraces/> },
 *   ]
 *   <SegmentedTabs tabs={TABS} />
 */

export type SegTab = {
  id: string
  label: string
  sub?: string
  render: () => React.ReactNode
}

export function SegmentedTabs({
  tabs,
  initial,
  className,
}: {
  tabs: SegTab[]
  initial?: string
  className?: string
}) {
  const [active, setActive] = useState(initial ?? tabs[0]?.id)
  const current = tabs.find((t) => t.id === active) ?? tabs[0]

  return (
    <div className={className}>
      <div className="mb-7 flex flex-col items-center gap-2">
        <div className="inline-flex flex-wrap items-center justify-center gap-1 rounded-full border border-ink-100 bg-paper p-1 shadow-sm">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setActive(t.id)}
              className={cn(
                'rounded-full px-4 py-2 text-[12.5px] font-medium transition',
                active === t.id
                  ? 'bg-ink-900 text-paper'
                  : 'text-ink-500 hover:text-ink-900',
              )}
            >
              {t.label}
            </button>
          ))}
        </div>
        {current?.sub && (
          <span className="font-mono text-[11px] text-ink-400">{current.sub}</span>
        )}
      </div>

      {current?.render()}
    </div>
  )
}
